<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { SatButtonMoney } from "@chatfood/design-system";
import { computed, ref, onMounted } from "vue";
import { useModalHubStore } from "../modal-hub";
import { GuestPrice } from "../guest-price";
import { useBasketStore } from "~/stores/basket";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useLocale } from "~/composables/use-locale";
import { useGuestStore } from "~/stores/guest";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useCustomerStore } from "~/stores/customer";
import { useNavigation } from "~/composables/use-navigation";
import { useFeatureFlag } from "#imports";

const css = bemBuilder("basket-view-checkout");

const { t } = useLocale();
const { isEnabled } = useFeatureFlag();

const business = useBusinessStore();
const basket = useBasketStore();
const outlet = useOutletStore();
const modalHub = useModalHubStore();
const customer = useCustomerStore();
const guest = useGuestStore();
const featureMatrix = useFeatureMatrixStore();
const { push } = useNavigation();

const isNewCheckoutFlow = ref(false);
const hasCrossSellFlow = ref(false);

const isDisabled = computed<boolean>(() => {
  if (outlet.isClosed) return true;

  if (
    outlet.orderingMode.outletState === "pre_order_only" &&
    !basket.scheduledTime
  ) {
    return true;
  }

  if (basket.isForDelivery && !basket.addressId && !basket.currentLocation) {
    return true;
  }

  return false;
});

const resolveFeatureFlags = async () => {
  isNewCheckoutFlow.value = await isEnabled("direct.useNewCheckoutFlow");
  hasCrossSellFlow.value =
    featureMatrix.isToggleEnabled("enable-floating-items-cross-sell") &&
    (await isEnabled("direct.useBasketCrossSell"));
};

onMounted(() => {
  if (!basket.isLoaded) {
    throw new Error(
      "BasketViewCheckout component requires basket store to be loaded.",
    );
  }

  resolveFeatureFlags();
});

const goToCheckout = async () => {
  if (isNewCheckoutFlow.value) {
    return await push("checkout", {});
  }

  modalHub.showCheckout();
};

const goToCrossSell = async () => {
  if (isNewCheckoutFlow.value && hasCrossSellFlow.value) {
    modalHub.showCrossSellList();
    return;
  }

  await goToCheckout();
};

const onCheckout = async () => {
  if (!customer.isLogged && !featureMatrix.skipAuthFlow) {
    modalHub.showAuthManager(async () => {
      if (!customer.isLogged && !guest.startedFlow) return;
      if (
        basket.orderingMode === "delivery" &&
        !basket.addressId &&
        !guest.address
      ) {
        modalHub.showAddressForm(async () => {
          if (basket.addressId || guest.address) {
            await goToCrossSell();
          }
        });
        return;
      }

      await goToCrossSell();
    }, true);
    return;
  }
  if (basket.orderingMode === "delivery") {
    if (!basket.addressId && customer.isLogged) {
      modalHub.showAddressForm(async () => {
        if (basket.addressId) {
          await goToCrossSell();
        }
      });
      return;
    }
    if (!guest.address && !customer.isLogged) {
      modalHub.showAddressForm(async () => {
        if (guest.address) {
          await goToCrossSell();
        }
      });
      return;
    }
    const address = computed(() => {
      return customer.addresses.find(
        (address) => address.id === basket.addressId,
      );
    });
    if (business.config.isPostalCodeRequired && !address.value?.zipCode) {
      modalHub.showAddressForm(async () => {
        if (basket.addressId && address.value?.zipCode) {
          await goToCrossSell();
        }
      });
      return;
    }
  }

  await goToCrossSell();
};
</script>

<template>
  <div :class="css()">
    <SatButtonMoney
      :price="basket.totalBreakdown.outstandingBalance"
      :locale="business.locale"
      :disabled="isDisabled"
      size="large"
      :hide-cents="false"
      @click="onCheckout"
    >
      {{ t("components.basket_view_checkout.cta") }}
    </SatButtonMoney>
    <GuestPrice />
  </div>
</template>

<style lang="scss" scoped>
.basket-view-checkout {
  display: flex;
  gap: var(--spacing-xs);
  flex-direction: column;
}
</style>
